import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  Menu, X, Zap, BookOpen, Briefcase, Users, BarChart2, LogIn, UserPlus, LayoutDashboard, UserCircle, LogOut, Shield
} from 'lucide-react';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const Navbar = ({ onActionClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinkClasses = ({ isActive }) =>
    `relative font-medium text-sm transition-colors duration-300 ${
      isActive ? 'text-primary' : 'text-foreground/80 hover:text-primary'
    }`;

  const mobileNavLinkClasses = ({ isActive }) =>
    `block py-3 text-lg transition-colors duration-300 ${
      isActive ? 'text-primary font-semibold' : 'text-foreground/80 hover:text-primary'
    }`;

  const navItems = [
    { name: 'Solutions', path: '/solutions', icon: Zap },
    { name: 'Documentation', path: '/documentation', icon: BookOpen },
    { name: 'Blog', path: '/blog', icon: BarChart2 },
    { name: 'Company', path: '/about', icon: Users },
    { name: 'Careers', path: '/careers', icon: Briefcase },
  ];

  const renderNavLinks = (isMobile = false) =>
    navItems.map((item) => (
      <li key={item.name}>
        <NavLink
          to={item.path}
          className={isMobile ? mobileNavLinkClasses : navLinkClasses}
          onClick={() => {
            if (item.path === '/documentation') {
              onActionClick('Documentation');
              return;
            }
            if (isOpen) setIsOpen(false);
          }}
        >
          {isMobile && <item.icon className="inline-block w-5 h-5 mr-3" />}
          {item.name}
        </NavLink>
      </li>
    ));

  const getAvatarFallback = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length > 1) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isOpen ? 'bg-background/95 shadow-md backdrop-blur-sm' : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center space-x-2" onClick={() => setIsOpen(false)}>
            <img src="https://storage.googleapis.com/hostinger-horizons-assets-prod/34687cef-4e75-459d-b6c8-7e9a5f71ffdb/acacfe7044a1c9c74538c545cc0cae87.png" alt="WebFiny Logo" className="h-8 w-auto" />
            <span className="text-xl font-bold text-foreground">WebFiny</span>
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            <ul className="flex items-center space-x-6">{renderNavLinks()}</ul>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                    <Avatar>
                      <AvatarImage src={profile?.avatar_url} alt={profile?.full_name} />
                      <AvatarFallback>{getAvatarFallback(profile?.full_name)}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{profile?.full_name}</p>
                      <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      <span>Dashboard</span>
                    </Link>
                  </DropdownMenuItem>
                  {profile?.role === 'Admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin">
                        <Shield className="mr-2 h-4 w-4" />
                        <span>Admin</span>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => onActionClick('Profile Settings')}>
                    <UserCircle className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center space-x-2">
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/login">
                    <LogIn className="w-4 h-4 mr-2" />
                    Login
                  </Link>
                </Button>
                <Button size="sm" asChild>
                  <Link to="/signup">
                    Sign Up <UserPlus className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            )}
          </div>

          <div className="md:hidden">
            <button onClick={() => setIsOpen(!isOpen)} className="text-foreground">
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background/95"
          >
            <div className="container mx-auto px-4 pb-8 pt-4">
              <ul className="space-y-4 mb-6">{renderNavLinks(true)}</ul>
              <div className="border-t border-border pt-6 space-y-3">
                {user ? (
                  <>
                    <Button className="w-full" size="lg" asChild>
                      <Link to="/dashboard" onClick={() => setIsOpen(false)}>
                        <LayoutDashboard className="w-5 h-5 mr-2" />
                        Dashboard
                      </Link>
                    </Button>
                    {profile?.role === 'Admin' && (
                      <Button variant="outline" className="w-full" size="lg" asChild>
                        <Link to="/admin" onClick={() => setIsOpen(false)}>
                          <Shield className="w-5 h-5 mr-2" />
                          Admin Panel
                        </Link>
                      </Button>
                    )}
                    <Button variant="destructive" className="w-full" size="lg" onClick={() => { handleSignOut(); setIsOpen(false); }}>
                      <LogOut className="w-5 h-5 mr-2" />
                      Log Out
                    </Button>
                  </>
                ) : (
                  <>
                    <Button className="w-full" size="lg" asChild>
                      <Link to="/login" onClick={() => setIsOpen(false)}>
                        <LogIn className="w-5 h-5 mr-2" />
                        Login
                      </Link>
                    </Button>
                    <Button variant="outline" className="w-full" size="lg" asChild>
                      <Link to="/signup" onClick={() => setIsOpen(false)}>
                        <UserPlus className="w-5 h-5 mr-2" />
                        Sign Up
                      </Link>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;